import { useEffect, useState } from "react";
import { confirmAction } from "../../utils/confirm";
import AppModal from "../../components/AppModal";
import PhoneInput from "../../components/PhoneInput";
import Pagination from "../../components/Pagination/Pagination";
import FilterBar, { FilterField } from "../../components/FilterBar";
import { useListController } from "../../controllers/useListController";
import { centresService } from "../../services/apiService";
import { usePermission } from "../../hooks/usePermission";
import { showToast } from "../../utils/toast";
import { validateForm } from "../../utils/validate";
import { formatPhone } from "../../utils/phone";
import { ACTIVE_STATUS_FILTERS, emptyMessageForStatus, statusFromFilter } from "../../utils/listFilters";

const STATUS_FILTERS = ACTIVE_STATUS_FILTERS("All centres");

const EMPTY = {
  id: null,
  name: "",
  suburb: "",
  address: "",
  phone: "",
  email: "",
  manager_name: "",
  manager_email: "",
  hours: "6:30am - 6:30pm",
  is_active: 1,
};

export default function Centres() {
  const { rows, pagination, loading, error, fetchRows } =
    useListController(centresService);
  const { can } = usePermission();
  const canAdd = can("centres", "is_add");
  const canEdit = can("centres", "is_update");
  const canDelete = can("centres", "is_delete");

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [viewFilter, setViewFilter] = useState("all");
  const [search, setSearch] = useState("");

  const queryParams = (extra = {}) => ({
    page: extra.page ?? pagination.page,
    limit: extra.limit ?? pagination.limit,
    status: statusFromFilter(extra.filter ?? viewFilter, STATUS_FILTERS),
    search: extra.search ?? search,
  });

  useEffect(() => {
    fetchRows({ page: 1, status: statusFromFilter(viewFilter, STATUS_FILTERS), search: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewFilter]);

  const onSearch = (e) => {
    e.preventDefault();
    fetchRows({ page: 1, status: statusFromFilter(viewFilter, STATUS_FILTERS), search });
  };

  const onReset = (e) => {
    e.preventDefault();
    setViewFilter("all");
    setSearch("");
    fetchRows({ page: 1, status: "", search: "" });
  };

  const handleStatusChange = (e) => {
    setViewFilter(e.target.value);
    setSearch("");
  };

  const emptyMessage = emptyMessageForStatus(viewFilter, {
    all: "No centres yet",
    active: "No active centres",
    inactive: "No inactive centres",
  });

  const openAdd = () => {
    setForm(EMPTY);
    setModalOpen(true);
  };
  const openEdit = (row) => {
    setForm({ ...EMPTY, ...row, phone: formatPhone(row.phone || "") });
    setModalOpen(true);
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? (checked ? 1 : 0) : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !validateForm(e.currentTarget, [
        { name: "name", value: form.name, label: "Centre name", required: true },
        { name: "suburb", value: form.suburb, label: "Suburb", required: true },
        { name: "address", value: form.address, label: "Address", required: true },
        { name: "phone", value: form.phone, label: "Phone", required: true, type: "auMobile" },
        { name: "email", value: form.email, label: "Centre email", required: true, type: "email" },
        { name: "manager_name", value: form.manager_name, label: "Manager name", required: true },
        { name: "manager_email", value: form.manager_email, label: "Manager email", required: true, type: "email" },
        { name: "hours", value: form.hours, label: "Hours", required: true },
      ])
    )
      return;
    setSubmitting(true);
    try {
      const payload = { ...form, phone: formatPhone(form.phone) };
      const res = form.id
        ? await centresService.update(payload)
        : await centresService.create(payload);
      if (res?.success) {
        showToast("success", form.id ? "Centre updated" : "Centre created");
        setModalOpen(false);
        fetchRows(queryParams());
      } else showToast("error", res?.msg || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    const r = await confirmAction({
      title: `Delete "${row.name}"?`,
      icon: "warning",
      confirmText: "Delete",
      variant: "danger",
    });
    if (!r.isConfirmed) return;
    const res = await centresService.delete(row.id);
    if (res?.success) {
      showToast("success", "Centre deleted");
      fetchRows(queryParams());
    } else showToast("error", res?.msg || "Failed");
  };

  return (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <strong>Centres</strong>
        {canAdd && (
          <button className="btn btn-primary" onClick={openAdd}>
            <i className="cil-plus me-1" /> Add Centre
          </button>
        )}
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger">{error}</div>}

        <FilterBar onSubmit={onSearch} onReset={onReset}>
          <FilterField id="centre-status-filter" label="Show">
            <select
              id="centre-status-filter"
              className="form-select"
              value={viewFilter}
              onChange={handleStatusChange}
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </FilterField>
          <FilterField id="centre-search" label="Search">
            <input
              id="centre-search"
              className="form-control"
              placeholder="Name, suburb or manager…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FilterField>
        </FilterBar>

        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Centre</th>
                <th>Suburb</th>
                <th>Manager</th>
                <th>Hours</th>
                <th>Status</th>
                <th style={{ width: 150 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={6} className="text-center py-4">Loading…</td></tr>)}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-4 text-muted">{emptyMessage}</td></tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div className="fw-semibold">{r.name}</div>
                      <div className="small text-muted">{r.email || ""}</div>
                    </td>
                    <td>{r.suburb || "—"}</td>
                    <td>
                      <div>{r.manager_name || "—"}</div>
                      <div className="small text-muted">{r.manager_email || ""}</div>
                    </td>
                    <td className="small">{r.hours}</td>
                    <td>
                      {r.is_active ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-secondary">Inactive</span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        {canEdit && (
                          <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(r)}>Edit</button>
                        )}
                        {canDelete && (
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(r)}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <Pagination
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          itemsCount={rows.length}
          loading={loading}
          onPageChange={(page) => fetchRows({ ...queryParams(), page })}
          onLimitChange={(limit) => fetchRows({ ...queryParams(), page: 1, limit })}
        />
      </div>

      <AppModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={form.id ? "Edit Centre" : "Add Centre"}
        size="lg"
        footer={
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" form="centre-form" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Saving…" : form.id ? "Save Changes" : "Create"}
            </button>
          </>
        }
      >
              <form id="centre-form" onSubmit={handleSubmit} noValidate>
                <div className="row g-3">
                  <div className="col-md-7">
                    <label className="form-label">Name <span className="text-danger">*</span></label>
                    <input name="name" className="form-control" value={form.name} onChange={onChange} required />
                  </div>
                  <div className="col-md-5">
                    <label className="form-label">Suburb <span className="text-danger">*</span></label>
                    <input name="suburb" className="form-control" value={form.suburb || ""} onChange={onChange} required />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Address <span className="text-danger">*</span></label>
                    <input name="address" className="form-control" value={form.address || ""} onChange={onChange} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Phone <span className="text-danger">*</span></label>
                    <PhoneInput name="phone" value={form.phone} onChange={onChange} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Centre email <span className="text-danger">*</span></label>
                    <input name="email" type="email" className="form-control" value={form.email || ""} onChange={onChange} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Manager name <span className="text-danger">*</span></label>
                    <input name="manager_name" className="form-control" value={form.manager_name || ""} onChange={onChange} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Manager email <span className="text-danger">*</span> <span className="text-muted small">(form alerts)</span></label>
                    <input name="manager_email" type="email" className="form-control" value={form.manager_email || ""} onChange={onChange} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Hours <span className="text-danger">*</span></label>
                    <input name="hours" className="form-control" value={form.hours || ""} onChange={onChange} required />
                  </div>
                </div>
                <div className="form-check mt-3">
                  <input type="checkbox" id="centreActive" className="form-check-input" name="is_active" checked={!!form.is_active} onChange={onChange} />
                  <label htmlFor="centreActive" className="form-check-label ms-1">Active</label>
                </div>
              </form>
      </AppModal>
    </div>
  );
}
